import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarExport,
} from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { Location } from 'fhir/r4b';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTER_PATH } from 'src/features/visits/in-person/routing/routesInPerson';
import { DEFAULT_BATCH_DAYS, splitDateRangeIntoBatches } from 'utils/lib/helpers/reports';
import { VisitStatusLabel } from 'utils/lib/types/api/appointment.types';
import { getAiAssistedEncountersReport } from '../../api/api';
import { useApiClients } from '../../hooks/useAppClients';
import PageContainer from '../../layout/PageContainer';
import { buildTrackingBoardPath } from './trackingBoardLink';

interface AiAssistedEncounterRow {
  id: string;
  appointmentId: string;
  patientId: string;
  patientName: string;
  dateOfBirth: string;
  visitStatus: VisitStatusLabel;
  appointmentTime: string;
  appointmentStart: string;
  location?: string;
  locationId?: string;
  attendingProvider?: string;
  visitType?: string;
  reason?: string;
  aiType?: string;
}

type DateRangeFilter =
  | 'today'
  | 'yesterday'
  | 'last-7-days'
  | 'last-7-days-excluding-today'
  | 'last-30-days'
  | 'custom'
  | 'customRange';

const getStatusColor = (
  status: VisitStatusLabel
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'pending':
      return 'default';
    case 'arrived':
      return 'info';
    case 'intake':
      return 'primary';
    case 'ready':
    case 'ready for provider':
      return 'warning';
    case 'provider':
      return 'secondary';
    case 'discharged':
      return 'success';
    case 'awaiting supervisor approval':
      return 'warning';
    default:
      return 'error';
  }
};

const useAiAssistedEncounters = (
  dateRange: DateRangeFilter,
  start: string,
  end: string,
  selectedLocationId: string
): ReturnType<typeof useQuery<AiAssistedEncounterRow[], Error>> => {
  const { oystehrZambda } = useApiClients();

  return useQuery({
    queryKey: ['ai-assisted-encounters', dateRange, start, end, selectedLocationId],
    queryFn: async (): Promise<AiAssistedEncounterRow[]> => {
      if (!oystehrZambda) {
        throw new Error('Oystehr client not available');
      }

      // Build location filter if a location is selected
      const locationIds = selectedLocationId !== 'all' ? [selectedLocationId] : undefined;

      // Calculate the date range in days
      const startDate = DateTime.fromISO(start);
      const endDate = DateTime.fromISO(end);
      const daysDifference = endDate.diff(startDate, 'days').days;

      console.log(`Date range is ${daysDifference.toFixed(2)} days`);

      // If the date range is <= DEFAULT_BATCH_DAYS days, make a single request
      if (daysDifference <= DEFAULT_BATCH_DAYS) {
        console.log('Making single request for date range');
        const response = await getAiAssistedEncountersReport(oystehrZambda, {
          dateRange: { start, end },
          locationIds,
        });

        // Transform the response to match our display requirements
        const processedEncounters: AiAssistedEncounterRow[] = response.encounters.map((encounter) => {
          const appointmentTime = encounter.appointmentStart
            ? DateTime.fromISO(encounter.appointmentStart).toFormat('MM/dd/yyyy hh:mm a')
            : 'Unknown';

          return {
            id: encounter.appointmentId,
            appointmentId: encounter.appointmentId,
            patientId: encounter.patientId,
            patientName: encounter.patientName,
            dateOfBirth: encounter.dateOfBirth,
            visitStatus: encounter.visitStatus as VisitStatusLabel,
            appointmentTime,
            appointmentStart: encounter.appointmentStart,
            location: encounter.location,
            locationId: encounter.locationId,
            attendingProvider: encounter.attendingProvider,
            visitType: encounter.visitType,
            reason: encounter.reason,
            aiType: encounter.aiType,
          };
        });

        return processedEncounters.sort((a, b) => {
          const aTime = a.appointmentStart;
          const bTime = b.appointmentStart;
          return DateTime.fromISO(bTime).toMillis() - DateTime.fromISO(aTime).toMillis();
        });
      }

      // Split the date range into DEFAULT_BATCH_DAYS-day batches
      const batches = splitDateRangeIntoBatches(start, end, DEFAULT_BATCH_DAYS);
      console.log(`Splitting date range into ${batches.length} batches of up to ${DEFAULT_BATCH_DAYS} days each`);

      // Fetch data for each batch in parallel
      const batchPromises = batches.map(async (batch, index) => {
        console.log(`Fetching batch ${index + 1}/${batches.length}: ${batch.start} to ${batch.end}`);
        const response = await getAiAssistedEncountersReport(oystehrZambda, {
          dateRange: batch,
          locationIds,
        });
        console.log(`Batch ${index + 1} returned ${response.encounters.length} encounters`);
        return response.encounters;
      });

      // Wait for all batches to complete
      const batchResults = await Promise.all(batchPromises);

      // Combine all encounters from all batches
      const allEncounters = batchResults.flat();
      console.log(`Total encounters across all batches: ${allEncounters.length}`);

      // Transform the combined response to match our display requirements
      const processedEncounters: AiAssistedEncounterRow[] = allEncounters.map((encounter) => {
        const appointmentTime = encounter.appointmentStart
          ? DateTime.fromISO(encounter.appointmentStart).toFormat('MM/dd/yyyy hh:mm a')
          : 'Unknown';

        return {
          id: encounter.appointmentId,
          appointmentId: encounter.appointmentId,
          patientId: encounter.patientId,
          patientName: encounter.patientName,
          dateOfBirth: encounter.dateOfBirth,
          visitStatus: encounter.visitStatus as VisitStatusLabel,
          appointmentTime,
          appointmentStart: encounter.appointmentStart,
          location: encounter.location,
          locationId: encounter.locationId,
          attendingProvider: encounter.attendingProvider,
          visitType: encounter.visitType,
          reason: encounter.reason,
          aiType: encounter.aiType,
        };
      });

      // Deduplicate by appointmentId (in case there are overlaps at batch boundaries)
      const uniqueEncounters = Array.from(new Map(processedEncounters.map((enc) => [enc.appointmentId, enc])).values());

      return uniqueEncounters.sort((a, b) => {
        const aTime = a.appointmentStart;
        const bTime = b.appointmentStart;
        return DateTime.fromISO(bTime).toMillis() - DateTime.fromISO(aTime).toMillis();
      });
    },
    enabled: Boolean(oystehrZambda && dateRange !== 'custom' && dateRange !== 'customRange'),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export default function AiAssistedEncounters(): React.ReactElement {
  const navigate = useNavigate();
  const { oystehr } = useApiClients();
  const [dateRange, setDateRange] = useState<DateRangeFilter>('today');
  const [customDate, setCustomDate] = useState<string>(DateTime.now().toFormat('yyyy-MM-dd'));
  const [customStartDate, setCustomStartDate] = useState<string>(DateTime.now().toFormat('yyyy-MM-dd'));
  const [customEndDate, setCustomEndDate] = useState<string>(DateTime.now().toFormat('yyyy-MM-dd'));
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState<boolean>(true);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('all');

  // Fetch locations on component mount
  useEffect(() => {
    const fetchLocations = async (): Promise<void> => {
      if (!oystehr) return;

      try {
        setLoadingLocations(true);
        const locationResults = await oystehr.fhir.search<Location>({
          resourceType: 'Location',
          params: [
            { name: '_count', value: '1000' },
            { name: '_sort', value: 'name' },
          ],
        });

        const locationsList = locationResults.unbundle();
        setLocations(locationsList);
      } catch (err) {
        console.error('Error fetching locations:', err);
      } finally {
        setLoadingLocations(false);
      }
    };

    void fetchLocations();
  }, [oystehr]);

  const getDateRange = useCallback(
    (filter: DateRangeFilter): { start: string; end: string } => {
      const now = DateTime.now().setZone('America/New_York');
      const today = now.startOf('day');

      switch (filter) {
        case 'today': {
          return {
            start: today.toISO() ?? '',
            end: today.endOf('day').toISO() ?? '',
          };
        }
        case 'yesterday': {
          const yesterday = today.minus({ days: 1 });
          return {
            start: yesterday.toISO() ?? '',
            end: yesterday.endOf('day').toISO() ?? '',
          };
        }
        case 'last-7-days': {
          return {
            start: today.minus({ days: 6 }).toISO() ?? '',
            end: today.endOf('day').toISO() ?? '',
          };
        }
        case 'last-7-days-excluding-today': {
          return {
            start: today.minus({ days: 6 }).toISO() ?? '',
            end: today.minus({ days: 1 }).endOf('day').toISO() ?? '',
          };
        }
        case 'last-30-days': {
          return {
            start: today.minus({ days: 29 }).toISO() ?? '',
            end: today.endOf('day').toISO() ?? '',
          };
        }
        case 'custom': {
          const customDateTime = DateTime.fromISO(customDate).setZone('America/New_York');
          return {
            start: customDateTime.startOf('day').toISO() ?? '',
            end: customDateTime.endOf('day').toISO() ?? '',
          };
        }
        case 'customRange': {
          const startDateTime = DateTime.fromISO(customStartDate).setZone('America/New_York');
          const endDateTime = DateTime.fromISO(customEndDate).setZone('America/New_York');
          return {
            start: startDateTime.startOf('day').toISO() ?? '',
            end: endDateTime.endOf('day').toISO() ?? '',
          };
        }
        default: {
          return {
            start: today.toISO() ?? '',
            end: today.endOf('day').toISO() ?? '',
          };
        }
      }
    },
    [customDate, customStartDate, customEndDate]
  );

  const { start, end } = getDateRange(dateRange);
  const {
    data: encounters = [],
    isLoading,
    error,
    refetch,
  } = useAiAssistedEncounters(dateRange, start, end, selectedLocationId);

  const handleBack = (): void => {
    navigate('/reports');
  };

  const handleDateRangeChange = (event: SelectChangeEvent<DateRangeFilter>): void => {
    setDateRange(event.target.value as DateRangeFilter);
  };

  const handleLocationFilterChange = (event: SelectChangeEvent<string>): void => {
    const newLocationId = event.target.value;
    setSelectedLocationId(newLocationId);
  };

  const handleCustomDateChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setCustomDate(event.target.value);
  };

  const handleCustomStartDateChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setCustomStartDate(event.target.value);
  };

  const handleCustomEndDateChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setCustomEndDate(event.target.value);
  };

  const handleRefresh = (): void => {
    void refetch();
  };

  // Custom toolbar component with export functionality
  const CustomToolbar = (): React.ReactElement => {
    return (
      <GridToolbarContainer>
        <GridToolbarExport csvOptions={{ fileName: 'ai-assisted-encounters-report' }} />
      </GridToolbarContainer>
    );
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'appointmentId',
        headerName: 'Appointment ID',
        width: 320,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const appointmentId = params.value;

          const linkPath = `/in-person/${appointmentId}/${ROUTER_PATH.REVIEW_AND_SIGN}`;

          return (
            <Link
              to={linkPath}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#1976d2',
                textDecoration: 'underline',
                fontFamily: 'monospace',
              }}
            >
              {appointmentId}
            </Link>
          );
        },
      },
      {
        field: 'aiType',
        headerName: 'AI Type',
        width: 250,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const aiType = params.value as string;
          if (!aiType) return <span>-</span>;

          // Determine chip color based on AI type
          let color: 'primary' | 'warning' | 'success' = 'primary';
          if (aiType === 'ambient scribe') {
            color = 'warning';
          } else if (aiType === 'patient HPI chatbot & ambient scribe') {
            color = 'success';
          }

          return <Chip label={aiType} color={color} size="small" variant="outlined" />;
        },
      },
      {
        field: 'visitStatus',
        headerName: 'Status',
        width: 160,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            label={params.value}
            color={getStatusColor(params.value as VisitStatusLabel)}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: 'appointmentTime',
        headerName: 'Appointment Time',
        width: 180,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const visitType = params.row.visitType;
          const locationId = params.row.locationId;
          const appointmentTime = params.value;
          const appointmentStart = params.row.appointmentStart;
          const visitStatus = params.row.visitStatus as VisitStatusLabel | undefined;

          // Handle different visit types
          if (visitType === 'In-Person' && locationId) {
            const trackingBoardPath = buildTrackingBoardPath({ appointmentStart, locationId, visitStatus });

            return (
              <Link
                to={trackingBoardPath}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#1976d2',
                  textDecoration: 'underline',
                }}
              >
                {appointmentTime}
              </Link>
            );
          }

          if (visitType === 'Telemed') {
            return (
              <Link
                to="/visits"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#1976d2',
                  textDecoration: 'underline',
                }}
              >
                {appointmentTime}
              </Link>
            );
          }

          // For visits without location or unknown type, just show the time without link
          return <span>{appointmentTime}</span>;
        },
      },
      {
        field: 'location',
        headerName: 'Location',
        width: 150,
        sortable: true,
      },
      {
        field: 'attendingProvider',
        headerName: 'Attending Provider',
        width: 200,
        sortable: true,
      },
      {
        field: 'visitType',
        headerName: 'Visit Type',
        width: 120,
        sortable: true,
      },
      {
        field: 'patientName',
        headerName: 'Patient Name',
        width: 200,
        sortable: true,
      },
      {
        field: 'reason',
        headerName: 'Reason',
        width: 200,
        sortable: true,
      },
    ],
    []
  );

  return (
    <PageContainer>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PsychologyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" color="primary.dark" fontWeight={600}>
              AI-Assisted Encounters
            </Typography>
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          This report shows encounters that have associated DocumentReference resources with AI-related types. Filter by
          date range and location to view appointments with AI-assisted documentation. Click an appointment ID to
          navigate to the specific appointment chart.
        </Typography>

        {/* Date and Location Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Date Range</InputLabel>
            <Select value={dateRange} label="Date Range" onChange={handleDateRangeChange}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="last-7-days">Last 7 Days</MenuItem>
              <MenuItem value="last-7-days-excluding-today">Last 7 Days (Excluding Today)</MenuItem>
              <MenuItem value="last-30-days">Last 30 Days</MenuItem>
              <MenuItem value="custom">Custom Date</MenuItem>
              <MenuItem value="customRange">Custom Date Range</MenuItem>
            </Select>
          </FormControl>

          {dateRange === 'custom' && (
            <TextField
              type="date"
              size="small"
              value={customDate}
              onChange={handleCustomDateChange}
              sx={{ minWidth: 160 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}

          {dateRange === 'customRange' && (
            <>
              <TextField
                type="date"
                size="small"
                label="Start Date"
                value={customStartDate}
                onChange={handleCustomStartDateChange}
                sx={{ minWidth: 160 }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                type="date"
                size="small"
                label="End Date"
                value={customEndDate}
                onChange={handleCustomEndDateChange}
                sx={{ minWidth: 160 }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </>
          )}

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Location</InputLabel>
            <Select
              value={selectedLocationId}
              label="Location"
              onChange={handleLocationFilterChange}
              disabled={loadingLocations}
            >
              <MenuItem value="all">All Locations</MenuItem>
              {locations
                .filter((loc) => loc.name)
                .map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={handleRefresh} disabled={isLoading} startIcon={<RefreshIcon />}>
            Refresh
          </Button>
        </Box>

        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGridPro
            rows={encounters}
            columns={columns}
            getRowId={(row) => row.appointmentId}
            loading={isLoading}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
              sorting: {
                sortModel: [{ field: 'appointmentTime', sort: 'desc' }],
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{
              toolbar: CustomToolbar,
            }}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
              },
            }}
          />
        </Paper>

        {error && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography color="error">
              Error loading encounters: {error instanceof Error ? error.message : 'Unknown error'}
            </Typography>
          </Box>
        )}

        {!isLoading && encounters.length === 0 && !error && (
          <Box sx={{ mt: 2, p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No AI-assisted encounters found for the selected filters
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              Try selecting a different date range or location, or check if there are any encounters with AI-generated
              documents
            </Typography>
          </Box>
        )}
      </Box>
    </PageContainer>
  );
}
